import { isPlatformServer } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, PLATFORM_ID, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { take } from 'rxjs';
import { MktDynamicOfferService } from 'src/app/core/services/mkt-dynamic-offer.service';
import { STATES } from './states.data.js';

let inputCount = 0;

@Component({
  selector: 'app-generic-template',
  templateUrl: './generic-template.component.html',
  styleUrls: ['./generic-template.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericTemplateComponent implements OnInit, AfterViewInit {
  public _content;
  public componentLoadComplete: boolean = false;
  public formGroups;
  public defaultFormGroup = new FormGroup({});
  public STATES = STATES;

  @Input('content')
  public set content(val) {
    this._content = val;
    this.ngOnInit();
    this.cdr.detectChanges();
  }
  public get content() {
    return this._content;
  }

  @ViewChild('heading') heading: TemplateRef<any>;
  @ViewChild('paragraph') paragraph: TemplateRef<any>;
  @ViewChild('list') list: TemplateRef<any>;
  @ViewChild('descriptionList') descriptionList: TemplateRef<any>;
  @ViewChild('blockquote') blockquote: TemplateRef<any>;
  @ViewChild('hr') hr: TemplateRef<any>;
  @ViewChild('formGroup') formGroup: TemplateRef<any>;
  @ViewChild('input') input: TemplateRef<any>;
  @ViewChild('radio') radio: TemplateRef<any>;
  @ViewChild('checkbox') checkbox: TemplateRef<any>;
  @ViewChild('dropdown') dropdown: TemplateRef<any>;
  @ViewChild('statesDropdown') statesDropdown: TemplateRef<any>;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer2: Renderer2,
    private mktDynamicOfferService: MktDynamicOfferService,
    public formBuilder: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.componentLoadComplete = false;
    this.formGroups = {};
  }

  ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      this.serverOnInit();
    }
    else {
      this.browserOnInit();
    }
  }

  commonOnInit() {
    // Set formGroups
    this.recursiveSearch('formGroup', this.content, (formGroup) => {
      const fg = {};
      formGroup?.inputs?.forEach(input => fg[input.id] = null);

      this.formGroups[formGroup.formGroupName] = this.formBuilder.group(fg);
    });
  }

  serverOnInit() {
    this.commonOnInit();
  }

  browserOnInit() {
    this.commonOnInit();
  }

  async ngAfterViewInit() {
    await Promise.all([
      this.setDynamicDates(),
      this.getAllDynamicOffer('', '', '', ''),
    ]);

    this.componentLoadComplete = true;
  }

  setDynamicDates(): Promise<any> {
    return new Promise((resolve, reject) => {
      const si = setInterval(() => {
        // if (!this.elem?.nativeElement?.children?.length) return;

        let domList = this.elementRef?.nativeElement.querySelectorAll("[data-id='currentYear']");
        [].slice.call(domList).forEach((elem: HTMLElement) => {
          const date = new Date();
          elem.innerHTML = date.getFullYear + '';
        });

        clearInterval(si);
        resolve(true);
      }, 250);
    });
  }

  private getAllDynamicOffer(cardName, pid, affinityId, category) {
    if(isPlatformServer(this.platformId)) return;
    return this.mktDynamicOfferService
      .getAllDynamicOffer('CitiAddInfoNGA', pid, affinityId, category)
      .pipe(take(1))
      .subscribe(dynamicOffer => {
        dynamicOffer?.pricingDetails?.forEach(obj => {
          this.injectDynamicText(obj, obj.productIdentifier, obj.productType);
        });
      });
  }

  private injectDynamicText(dynamicData, pid, productType=null) {
    if (!dynamicData) return;

    for (const propName of Object.keys(dynamicData)) {
      let val = dynamicData[propName];

      if (propName === 'annualFeeAmount') {
        if (
          val === null ||
          val === undefined ||
          val === '0'
        ) {
          continue;
        }
      }

      let tempKey = propName + '-' + pid;
      if (productType) tempKey = tempKey+'_'+productType;
      const dynamicVariableElementList = this.elementRef.nativeElement.querySelectorAll(`[data-id="${tempKey}"]`);

      for (let i = 0; i < dynamicVariableElementList.length; i++) {
        const dynamicVariableSpanElement = dynamicVariableElementList[i];
        this.renderer2.setProperty(dynamicVariableSpanElement, 'innerHTML', this.formatData(propName, val));
      }

      if (propName === 'rewardPromotion') {
        const rewardsObj = !!val && { ...val, ...val?.rewardPromotionDetail[0] } || {};
        this.injectDynamicText(rewardsObj, pid, productType);
      }
    }
  }

  formatData(key, value) {
    switch (key) {
      case 'balconMinimumFee':
      case 'accountLevelBalconMinimumFee':  
        value = value * 10;
        break;
      case 'balconFeeRate':
        value = value / 10;
        break;
      case 'accountLevelBalconFeeRate':
        value = value / 10;
        break;
      case 'bonusAmount':
      case 'hurdleAmount':
        value = this.formatDollar(value);
        break;
      default:
        value = value ?? '';
    }
    return value;
  }

  formatDollar(amt: string) {
    amt =
      amt !== undefined ? amt.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') : '';
    return amt;
  }

  recursiveSearch(targetKey: string, obj: any, callback: Function) {
    if (!obj) return;

    (obj.children || obj.listItems || obj.inputs || []).forEach(item => {
      if (item.type === targetKey) callback(item);
      else this.recursiveSearch(targetKey, item, callback);
    });
  }

  public getInputCount(input) {
    if (!input) input = {};
    if (!input?.index) input.index = inputCount++;
    return input.index;
  }

  limitInputLength(event: MouseEvent) {
    const domElem = <HTMLInputElement>event.currentTarget;
    const type = domElem.getAttribute('type');
    const maxlength = domElem.getAttribute('maxlength');

    if (type !== 'number' || !maxlength) return;

    const valLength = (domElem.value + '').length;
    if (valLength > +maxlength) domElem.value = domElem.value.slice(0, +maxlength);
  }
}
